import { get, writable } from 'svelte/store';
import Keyboard from './Keyboard.svelte';
export * from './layouts';
export const keyboardSpecialKeyStore = writable();
/**
 * Store that contains the current info of the keyboard config.
 */
export const KeyboardConfig = (() => {
    const config = writable({
        baseConfig: {},
        currentNode: null,
        currentNodeConfig: null
    });
    const setNode = (node, nodeConfig) => config.update((config) => ({
        ...config,
        ...nodeConfig,
        currentNodeConfig: {
            ...config.baseConfig,
            ...config.currentNodeConfig,
            ...nodeConfig,
            layout: nodeConfig?.layout ?? config.currentNodeConfig?.layout ?? config.baseConfig.layout
        },
        currentNode: node
    }));
    const removeNode = () => {
        config.update((config) => {
            config.currentNode = null;
            config.currentNodeConfig = null;
            return config;
        });
    };
    /**
     * Focuses the next input by dom order.
     */
    const focusNext = () => {
        const conf = get(config);
        if (conf.currentNode) {
            const allInputs = document.querySelectorAll('input, textarea');
            allInputs.forEach((input, index) => {
                if (input === conf.currentNode) {
                    if (allInputs[index + 1]) {
                        conf.currentNode.blur();
                        allInputs[index + 1].focus();
                    }
                }
            });
        }
    };
    return { ...config, setNode, removeNode, focusNext };
})();
/**
 * Use this function on a node to create a virtual keyboard as soon as focused.
 * @example
 * <input type="text" use:keyboard />
 *
 * <input type="text" use:keyboard={{ rootId: "#keyboard-root" }} />
 * <div id="keyboard-root"></div>
 *
 * <input type="text" use:keyboard={{ language: "DE", layout: "full" }} />
 */
export function keyboard(node, config) {
    let vboard;
    let scrollOffset = 0;
    node.addEventListener('focus', () => {
        let keyboardInstanceSymbol = undefined;
        // If keyboard should be attached, it will be attached to the bottom of the input
        if (config?.attach) {
            const top = node.getBoundingClientRect().bottom;
            const rootElement = document.getElementById('vboard-container-fixed') ?? document.createElement('div');
            rootElement.style.position = 'absolute';
            rootElement.style.top = `${top + 10}px`;
            rootElement.style.left = '0';
            rootElement.style.width = '100%';
            rootElement.style.zIndex = '9999';
            rootElement.id = 'vboard-container-fixed';
            document.body.appendChild(rootElement);
            if (!rootElement) {
                throw new Error('Root element for keyboard not found');
            }
            keyboardInstanceSymbol = Symbol();
            vboard = new Keyboard({
                target: rootElement,
                props: {
                    keyboardInstanceSymbol,
                    currentNode: node
                }
            });
            setTimeout(() => {
                rootElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, config?.transitionDuration ?? 210);
        }
        get(KeyboardConfig).activeKeyboardSymbol = keyboardInstanceSymbol;
        KeyboardConfig.setNode(node, config);
        setTimeout(() => {
            // Scroll into view if the input is not visible and keyboard fully present
            const keyboardHeight = document.querySelector('.vboard-container-fixed')?.getBoundingClientRect().height ?? 0;
            const { bottom, top } = node.getBoundingClientRect();
            const maxBottomPosition = window.innerHeight - keyboardHeight;
            if (bottom > maxBottomPosition) {
                scrollOffset = maxBottomPosition - bottom;
                window.scrollTo({ top: window.scrollY - scrollOffset, behavior: 'smooth' });
            }
            else if (top < 0) {
                scrollOffset = top;
                window.scrollTo({ top: window.scrollY - scrollOffset, behavior: 'smooth' });
            }
            // Reset our scroll offset if user scrolled himself
            window.addEventListener('scroll', () => {
                scrollOffset = 0;
            }, { once: true });
        }, config?.transitionDuration ?? 210);
    });
    node.addEventListener('blur', () => {
        vboard?.$destroy();
        get(KeyboardConfig).activeKeyboardSymbol = null;
        KeyboardConfig.removeNode();
        // Reset previous scroll
        if (scrollOffset !== 0) {
            window.scrollTo({ top: window.scrollY + scrollOffset, behavior: 'smooth' });
            scrollOffset = 0;
        }
    });
}
